import type {
  KeyboardEventHandler,
  MouseEventHandler,
  PointerEventHandler,
  TouchEventHandler
} from 'react';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader,
  PlayerSubheader
} from '@library/components/player';
import { memo, useCallback } from 'react';
import { CSS } from '@dnd-kit/utilities';
import DraggableIcon from '@library/assets/images/Draggable.png';
import { IconButton } from '@library/components/buttons';
import { MoreLessButton } from '@bo-components/playerPacks/playerPackCreate/MoreLessButton';
import type { OddsType } from '@library/types/projection';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import type { PlayerPackFormValues } from '@library/types/playerPacks/PlayerPackFormSchema';
import type { PlayerPackProjection } from '@library/types/playerPacks/playerPacks';
import type { UseFormSetValue } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useSortable } from '@dnd-kit/sortable';

export interface PlayerPackPickedProjectionRowProps {
  projection: PlayerPackProjection;
  onRemoveProjection: (projection: PlayerPackProjection) => void;
  setValue: UseFormSetValue<PlayerPackFormValues>;
  temporaryRank: number;
}

export const PlayerPackPickedProjectionRow = memo(
  ({
    projection,
    onRemoveProjection,
    setValue,
    temporaryRank
  }: PlayerPackPickedProjectionRowProps) => {
    const handleRemoveProjection = useCallback(() => {
      onRemoveProjection(projection);
    }, [onRemoveProjection, projection]);

    const handleClickMore = useCallback(() => {
      setValue(`picks.${temporaryRank}.projectionId`, projection.projectionId);
      setValue(`picks.${temporaryRank}.wagerType`, 'more', {
        shouldValidate: true,
        shouldDirty: true
      });
    }, [setValue, projection.projectionId, temporaryRank]);

    const handleClickLess = useCallback(() => {
      setValue(`picks.${temporaryRank}.projectionId`, projection.projectionId);
      setValue(`picks.${temporaryRank}.wagerType`, 'less', {
        shouldValidate: true,
        shouldDirty: true
      });
    }, [setValue, projection.projectionId, temporaryRank]);

    // Setup drag-and-drop for the row using useSortable
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: projection.projectionId });

    // Apply inline styles for transform and transition
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1 // Change opacity when dragging
    };

    // Explicitly cast event listeners to the correct types
    const onPointerDown = listeners?.onPointerDown as PointerEventHandler;
    const onMouseDown = listeners?.onMouseDown as MouseEventHandler;
    const onKeyDown = listeners?.onKeyDown as KeyboardEventHandler;
    const onTouchStart = listeners?.onTouchStart as TouchEventHandler;

    return (
      <tr
        key={projection?.projectionId}
        className="relative h-5 text-white"
        style={style}
      >
        <td className="p-4">
          <div
            ref={setNodeRef}
            role="button"
            tabIndex={0}
            aria-pressed={attributes['aria-pressed']}
            onPointerDown={onPointerDown} // Pass event handlers explicitly
            onMouseDown={onMouseDown}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
          >
            <PlayerProfile
              className="flex min-w-52 items-center gap-4 p-0"
              imageUrl={projection.playerImageUrl}
              name={projection.playerName}
              subheader={`${projection.projectionValue} ${projection.statType}`}
              secondaryHeader={projection.gameDisplay}
            >
              <img src={DraggableIcon} alt="Draggable" className="size-4" />
              <PlayerImage className="bg-soFresh-120 size-[49px] rounded-lg" />
              <div className="flex h-12 flex-col items-start justify-start">
                <PlayerName className="font-heebo text-soClean-100 text-center text-sm font-medium leading-tight tracking-tight" />
                <div className="flex flex-row items-center gap-4">
                  <PlayerSubheader className="text-soClean-100 text-xs font-medium leading-4 tracking-tight" />
                  <OddsTypeIndicator
                    oddsType={projection.oddsType as OddsType}
                    className="size-3"
                  />
                </div>
                <PlayerSecondaryHeader className="text-soClean-170 font-heebo text-xs font-light leading-4 tracking-tight" />
              </div>
            </PlayerProfile>
          </div>
        </td>
        <td className="p-4 text-left">{projection.projectionValue}</td>
        <td className="p-4 text-left">{projection.statType}</td>
        <td className="p-4 text-left align-middle">
          <MoreLessButton
            oddsType={projection.oddsType as OddsType}
            onClickLess={handleClickLess}
            onClickMore={handleClickMore}
          />
        </td>
        <td>
          <div className="bg-soFresh-150 relative top-1/2 z-10 inline-flex size-8 items-center justify-center rounded-md">
            <IconButton
              handleOnClick={handleRemoveProjection}
              className="bg-soFresh-110 rounded-md p-2"
            >
              <XMarkIcon
                aria-hidden="true"
                className="text-soClean-170 size-5"
                data-testid="XMarkIcon"
              />
            </IconButton>
          </div>
        </td>
      </tr>
    );
  }
);
